import type { Layout } from "@coral-xyz/borsh";
import { struct, u8, u64 } from "@coral-xyz/borsh";
import { createTransferCheckedInstruction, TOKEN_PROGRAM_ID } from "@solana/spl-token0.3.8";
import type { Connection } from "@solana/web3.js";
import { PublicKey, SYSVAR_CLOCK_PUBKEY, SYSVAR_RENT_PUBKEY, TransactionInstruction } from "@solana/web3.js";
import { STEP_FEE_ADDRESS, SYSTEM_PROGRAM_ID } from "@stepfinance/common";
import type { BaseRaydiumFarmPosition, FusionRaydiumFarmPosition } from "@stepfinance/farms";
import {
  USER_STAKE_INFO_ACCOUNT_LAYOUT,
  USER_STAKE_INFO_ACCOUNT_LAYOUT_V4,
  USER_STAKE_INFO_ACCOUNT_LAYOUT_V5,
} from "@stepfinance/farms";
import BN from "bn.js";

import { TokenChangeNotificationDisplay } from "../components/Notifications/TokenChangeNotificationDisplay";
import { confirmOptions } from "../constants/transaction";
import { TokenAmount } from "../utils/raydium/safe-math";
import { findAssociatedStakeInfoAddress } from "../utils/raydium/web3";
import { sendTransaction } from "../utils/transactions";
import { createUninitializedAccount, findOrCreateAccountByMint } from "./account";
import { calculateFeeToTake, createInstructionsToTakeFee } from "./fee";

function depositInstruction(
  programId: PublicKey,
  // staking pool
  poolId: PublicKey,
  poolAuthority: PublicKey,
  // user
  userInfoAccount: PublicKey,
  userOwner: PublicKey,
  userLpTokenAccount: PublicKey,
  poolLpTokenAccount: PublicKey,
  userRewardTokenAccount: PublicKey,
  poolRewardTokenAccount: PublicKey,
  // tokenProgramId: PublicKey,
  amount: number,
) {
  const dataLayout: Layout<{
    instruction: number;
    amount: BN;
  }> = struct([u8("instruction"), u64("amount")]);

  const keys = [
    { pubkey: poolId, isSigner: false, isWritable: true },
    { pubkey: poolAuthority, isSigner: false, isWritable: false },
    { pubkey: userInfoAccount, isSigner: false, isWritable: true },
    { pubkey: userOwner, isSigner: true, isWritable: false },
    { pubkey: userLpTokenAccount, isSigner: false, isWritable: true },
    { pubkey: poolLpTokenAccount, isSigner: false, isWritable: true },
    { pubkey: userRewardTokenAccount, isSigner: false, isWritable: true },
    { pubkey: poolRewardTokenAccount, isSigner: false, isWritable: true },
    { pubkey: SYSVAR_CLOCK_PUBKEY, isSigner: false, isWritable: false },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
  ];

  const data = Buffer.alloc(dataLayout.span);
  dataLayout.encode(
    {
      instruction: 10,
      amount: new BN(amount),
    },
    data,
  );

  return new TransactionInstruction({
    keys,
    programId,
    data,
  });
}

function depositInstructionV4(
  programId: PublicKey,
  // staking pool
  poolId: PublicKey,
  poolAuthority: PublicKey,
  // user
  userInfoAccount: PublicKey,
  userOwner: PublicKey,
  userLpTokenAccount: PublicKey,
  poolLpTokenAccount: PublicKey,
  userRewardTokenAccount: PublicKey,
  poolRewardTokenAccount: PublicKey,
  userRewardTokenAccountB: PublicKey,
  poolRewardTokenAccountB: PublicKey,
  // tokenProgramId: PublicKey,
  amount: number,
) {
  const dataLayout: Layout<{
    instruction: number;
    amount: BN;
  }> = struct([u8("instruction"), u64("amount")]);

  const keys = [
    { pubkey: poolId, isSigner: false, isWritable: true },
    { pubkey: poolAuthority, isSigner: false, isWritable: false },
    { pubkey: userInfoAccount, isSigner: false, isWritable: true },
    { pubkey: userOwner, isSigner: true, isWritable: false },
    { pubkey: userLpTokenAccount, isSigner: false, isWritable: true },
    { pubkey: poolLpTokenAccount, isSigner: false, isWritable: true },
    { pubkey: userRewardTokenAccount, isSigner: false, isWritable: true },
    { pubkey: poolRewardTokenAccount, isSigner: false, isWritable: true },
    { pubkey: SYSVAR_CLOCK_PUBKEY, isSigner: false, isWritable: false },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: userRewardTokenAccountB, isSigner: false, isWritable: true },
    { pubkey: poolRewardTokenAccountB, isSigner: false, isWritable: true },
  ];

  const data = Buffer.alloc(dataLayout.span);
  dataLayout.encode(
    {
      instruction: 1,
      amount: new BN(amount),
    },
    data,
  );

  return new TransactionInstruction({
    keys,
    programId,
    data,
  });
}

export function depositInstructionV5(
  programId: PublicKey,
  // staking pool
  poolId: PublicKey,
  poolAuthority: PublicKey,
  // user
  userAssociatedInfoAccount: PublicKey,
  userInfoAccounts: PublicKey[],
  userOwner: PublicKey,
  userLpTokenAccount: PublicKey,
  poolLpTokenAccount: PublicKey,
  userRewardTokenAccount: PublicKey,
  poolRewardTokenAccount: PublicKey,
  userRewardTokenAccountB: PublicKey,
  poolRewardTokenAccountB: PublicKey,
  // tokenProgramId: PublicKey,
  amount: number,
): TransactionInstruction {
  const dataLayout: Layout<{
    instruction: number;
    amount: BN;
  }> = struct([u8("instruction"), u64("amount")]);

  const keys = [
    { pubkey: poolId, isSigner: false, isWritable: true },
    { pubkey: poolAuthority, isSigner: false, isWritable: false },
    { pubkey: userAssociatedInfoAccount, isSigner: false, isWritable: true },
    { pubkey: userOwner, isSigner: true, isWritable: false },
    { pubkey: userLpTokenAccount, isSigner: false, isWritable: true },
    { pubkey: poolLpTokenAccount, isSigner: false, isWritable: true },
    { pubkey: userRewardTokenAccount, isSigner: false, isWritable: true },
    { pubkey: poolRewardTokenAccount, isSigner: false, isWritable: true },
    { pubkey: SYSVAR_CLOCK_PUBKEY, isSigner: false, isWritable: false },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: userRewardTokenAccountB, isSigner: false, isWritable: true },
    { pubkey: poolRewardTokenAccountB, isSigner: false, isWritable: true },
  ];

  for (const userInfoAccount of userInfoAccounts) {
    keys.push({ pubkey: userInfoAccount, isSigner: false, isWritable: true });
  }

  const data = Buffer.alloc(dataLayout.span);
  dataLayout.encode(
    {
      instruction: 11,
      amount: new BN(amount),
    },
    data,
  );

  return new TransactionInstruction({
    keys,
    programId,
    data,
  });
}

export async function deposit(
  connection: Connection | undefined | null,
  wallet: any | undefined | null,
  farmInfo: BaseRaydiumFarmPosition,
  lpAccount: PublicKey | undefined,
  rewardAccount: PublicKey | undefined,
  infoAccount: string,
  amount: string,
  awaitConfirmation = true,
  pendingReward?: number,
): Promise<string> {
  if (!connection || !wallet) throw new Error("Missing connection");
  if (!farmInfo) throw new Error("Missing pool information");
  if (!amount) throw new Error("Missing amount information");
  const instructions: TransactionInstruction[] = [];
  const signers: any = [];
  const owner = wallet.publicKey;
  let userLpAccount: PublicKey | undefined = lpAccount;
  if (!lpAccount) {
    userLpAccount = await findOrCreateAccountByMint(
      connection,
      owner,
      owner,
      instructions,
      farmInfo.version === 2
        ? new PublicKey(farmInfo.rewards[0].rewardToken.address)
        : new PublicKey(farmInfo.lpMintAddress),
    );
  }

  const userRewardTokenAccount = await findOrCreateAccountByMint(
    connection,
    owner,
    owner,
    instructions,
    new PublicKey(farmInfo.rewards[0].rewardToken.address),
  );

  const programId = new PublicKey(farmInfo.programId);
  let userInfoAccount: PublicKey;
  if (!infoAccount) {
    userInfoAccount = createUninitializedAccount(
      instructions,
      owner,
      await connection.getMinimumBalanceForRentExemption(USER_STAKE_INFO_ACCOUNT_LAYOUT.span),
      signers,
    );
  } else {
    userInfoAccount = new PublicKey(infoAccount);
  }

  let value = 0;
  if (Number(amount) > 0) {
    value = new TokenAmount(amount, farmInfo.lpDecimals, false).wei.toNumber();
  }

  instructions.push(
    depositInstruction(
      programId,
      new PublicKey(farmInfo.poolId),
      new PublicKey(farmInfo.poolAuthority),
      userInfoAccount,
      wallet.publicKey,
      userLpAccount as PublicKey,
      new PublicKey(farmInfo.poolLpTokenAccount),
      userRewardTokenAccount,
      new PublicKey(farmInfo.poolRewardTokenAccount),
      value,
    ),
  );

  const { createAccountInstructions, feeInstructions } = await createInstructionsToTakeFee(
    connection,
    wallet,
    new PublicKey(farmInfo.rewards[0].rewardToken.address),
    farmInfo.rewards[0].rewardToken.decimals,
    pendingReward,
    userRewardTokenAccount,
  );

  const rewardMint = farmInfo.rewards[0].rewardToken.address;
  const rewardAmount = farmInfo.rewards[0].rewardBalance;

  return await sendTransaction(
    connection,
    wallet,
    [...instructions, ...createAccountInstructions, ...feeInstructions],
    signers,
    awaitConfirmation,
    confirmOptions,
    true,
    {
      inProgress: {
        message: "You are claiming rewards",
        description: "Confirmation is in progress.",
      },
      success: {
        message: "You claimed rewards",
        description: (
          <>
            <span>You claimed:</span>
            <TokenChangeNotificationDisplay amount={rewardAmount} mintAddress={rewardMint} type="increase" />
          </>
        ),
      },
    },
  );
}

export async function depositV4(
  connection: Connection | undefined | null,
  wallet: any | undefined | null,
  farmInfo: FusionRaydiumFarmPosition,
  lpAccount: PublicKey | undefined,
  infoAccount: string | undefined,
  amount: string,
  awaitConfirmation = true,
  pendingReward?: number,
  pendingRewardDual?: number,
) {
  if (!connection || !wallet) throw new Error("Missing connection");
  if (!farmInfo) throw new Error("Missing pool information");
  if (!amount) throw new Error("Missing amount information");

  const signers: any = [];
  const instructions: TransactionInstruction[] = [];
  const owner = wallet.publicKey;

  if (!lpAccount) {
    lpAccount = await findOrCreateAccountByMint(
      connection,
      wallet.publicKey,
      wallet.publicKey,
      instructions,
      new PublicKey(farmInfo.lpDecimals),
    );
  }

  let userInfoAccount: PublicKey;
  if (!infoAccount) {
    userInfoAccount = createUninitializedAccount(
      instructions,
      owner,
      await connection.getMinimumBalanceForRentExemption(USER_STAKE_INFO_ACCOUNT_LAYOUT_V4.span),
      signers,
    );
  } else {
    userInfoAccount = new PublicKey(infoAccount);
  }
  const programId = new PublicKey(farmInfo.programId);

  const userRewardTokenAccount = await findOrCreateAccountByMint(
    connection,
    owner,
    owner,
    instructions,
    new PublicKey(farmInfo.rewards[0].rewardToken.address),
  );

  const userRewardTokenAccountB = await findOrCreateAccountByMint(
    connection,
    owner,
    owner,
    instructions,
    new PublicKey(farmInfo.rewards[1].rewardToken.address),
  );

  let value = 0;
  if (Number(amount) > 0) {
    value = new TokenAmount(amount, farmInfo.lpDecimals, false).wei.toNumber();
  }

  instructions.push(
    depositInstructionV4(
      programId,
      new PublicKey(farmInfo.poolId),
      new PublicKey(farmInfo.poolAuthority),
      userInfoAccount,
      wallet.publicKey,
      lpAccount,
      new PublicKey(farmInfo.poolLpTokenAccount),
      userRewardTokenAccount,
      new PublicKey(farmInfo.poolRewardTokenAccount),
      userRewardTokenAccountB,
      new PublicKey(farmInfo.poolRewardTokenAccountB),
      value,
    ),
  );

  const { createAccountInstructions, feeInstructions } = await createInstructionsToTakeFee(
    connection,
    wallet,
    new PublicKey(farmInfo.rewards[1].rewardToken.address),
    farmInfo.rewards[1].rewardToken.decimals,
    pendingReward,
    userRewardTokenAccountB,
  );

  if (pendingRewardDual) {
    // ...is dual pool
    const addressForPC = await findOrCreateAccountByMint(
      connection,
      wallet.publicKey,
      STEP_FEE_ADDRESS,
      createAccountInstructions,
      new PublicKey(farmInfo.rewards[0].rewardToken.address),
    );

    const { fee } = calculateFeeToTake(pendingRewardDual, farmInfo.rewards[0].rewardToken.decimals);

    feeInstructions.push(
      createTransferCheckedInstruction(
        userRewardTokenAccount,
        new PublicKey(farmInfo.rewards[0].rewardToken.address),
        addressForPC,
        wallet.publicKey,
        fee,
        farmInfo.rewards[0].rewardToken.decimals,
        [],
        TOKEN_PROGRAM_ID,
      ),
    );
  }

  const rewardMintA = farmInfo.rewards[0].rewardToken.address;
  const rewardMintB = farmInfo.rewards[1].rewardToken.address;

  const rewardAmountA = farmInfo.rewards[0].rewardBalance;
  const rewardAmountB = farmInfo.rewards[1].rewardBalance;

  return await sendTransaction(
    connection,
    wallet,
    [...instructions, ...createAccountInstructions, ...feeInstructions],
    signers,
    awaitConfirmation,
    confirmOptions,
    true,
    {
      inProgress: {
        message: "You are claiming rewards",
        description: "Confirmation is in progress.",
      },
      success: {
        message: "You claimed rewards",
        description: (
          <>
            <span>You claimed:</span>
            <TokenChangeNotificationDisplay amount={rewardAmountA} mintAddress={rewardMintA} type="increase" />
            <TokenChangeNotificationDisplay amount={rewardAmountB} mintAddress={rewardMintB} type="increase" />
          </>
        ),
      },
    },
  );
}
export async function depositV5(
  connection: Connection | undefined | null,
  wallet: any | undefined | null,
  farmInfo: FusionRaydiumFarmPosition,
  lpAccount: PublicKey | undefined,
  infoAccount: string | undefined,
  amount: string,
  awaitConfirmation = true,
  pendingReward?: number | null,
  pendingRewardDual?: number,
) {
  if (!connection || !wallet) throw new Error("Missing connection");
  if (!farmInfo) throw new Error("Missing pool information");
  if (!amount) throw new Error("Missing amount information");
  const signers: any = [];
  const instructions: TransactionInstruction[] = [];
  const owner = wallet.publicKey;

  if (!lpAccount) {
    lpAccount = await findOrCreateAccountByMint(
      connection,
      wallet.publicKey,
      wallet.publicKey,
      instructions,
      new PublicKey(farmInfo.lpMintAddress),
    );
  }
  let userInfoAccount: PublicKey;
  if (!infoAccount) {
    userInfoAccount = createUninitializedAccount(
      instructions,
      owner,
      await connection.getMinimumBalanceForRentExemption(USER_STAKE_INFO_ACCOUNT_LAYOUT_V5.span),
      signers,
    );
  } else {
    userInfoAccount = new PublicKey(infoAccount);
  }
  const programId = new PublicKey(farmInfo.programId);

  const userRewardTokenAccount = await findOrCreateAccountByMint(
    connection,
    owner,
    owner,
    instructions,
    new PublicKey(farmInfo.rewards[0].rewardToken.address),
  );

  const userRewardTokenAccountB = await findOrCreateAccountByMint(
    connection,
    owner,
    owner,
    instructions,
    new PublicKey(farmInfo.rewards[1].rewardToken.address),
  );

  let value = 0;
  if (Number(amount) > 0) {
    value = new TokenAmount(amount, farmInfo.lpDecimals, false).wei.toNumber();
  }
  const poolId = new PublicKey(farmInfo.poolId);
  const pda = await findAssociatedStakeInfoAddress(poolId, wallet.publicKey, programId);
  // if no associated userinfo account, create new one
  if (!pda.equals(userInfoAccount)) {
    instructions.push(createAssociatedLedgerAccountInstructionV5(programId, poolId, pda, wallet.publicKey));
  }
  const auxilaryStakingAccounts = pda.equals(userInfoAccount) ? [] : [userInfoAccount];
  instructions.push(
    depositInstructionV5(
      programId,
      new PublicKey(farmInfo.poolId),
      new PublicKey(farmInfo.poolAuthority),
      pda,
      auxilaryStakingAccounts,
      wallet.publicKey,
      lpAccount,
      new PublicKey(farmInfo.poolLpTokenAccount),
      userRewardTokenAccount,
      new PublicKey(farmInfo.poolRewardTokenAccount),
      userRewardTokenAccountB,
      new PublicKey(farmInfo.poolRewardTokenAccountB),
      value,
    ),
  );

  const { createAccountInstructions, feeInstructions } = await createInstructionsToTakeFee(
    connection,
    wallet,
    new PublicKey(farmInfo.rewards[1].rewardToken.address),
    farmInfo.rewards[1].rewardToken.decimals,
    pendingReward,
    userRewardTokenAccountB,
  );

  if (pendingRewardDual) {
    // ...is dual pool
    const addressForPC = await findOrCreateAccountByMint(
      connection,
      wallet.publicKey,
      STEP_FEE_ADDRESS,
      createAccountInstructions,
      new PublicKey(farmInfo.rewards[0].rewardToken.address),
    );

    const { fee } = calculateFeeToTake(pendingRewardDual, farmInfo.rewards[0].rewardToken.decimals);

    feeInstructions.push(
      createTransferCheckedInstruction(
        userRewardTokenAccount,
        new PublicKey(farmInfo.rewards[0].rewardToken.address),
        addressForPC,
        wallet.publicKey,
        fee,
        farmInfo.rewards[0].rewardToken.decimals,
        [],
        TOKEN_PROGRAM_ID,
      ),
    );
  }

  const rewardMintA = farmInfo.rewards[0].rewardToken.address;
  const rewardMintB = farmInfo.rewards[1].rewardToken.address;

  const rewardAmountA = farmInfo.rewards[0].rewardBalance;
  const rewardAmountB = farmInfo.rewards[1].rewardBalance;

  return await sendTransaction(
    connection,
    wallet,
    [...instructions, ...createAccountInstructions, ...feeInstructions],
    signers,
    awaitConfirmation,
    confirmOptions,
    true,
    {
      inProgress: {
        message: "You are claiming rewards",
        description: "Confirmation is in progress.",
      },
      success: {
        message: "You claimed rewards",
        description: (
          <>
            <span>You claimed:</span>
            <TokenChangeNotificationDisplay amount={rewardAmountA} mintAddress={rewardMintA} type="increase" />
            <TokenChangeNotificationDisplay amount={rewardAmountB} mintAddress={rewardMintB} type="increase" />
          </>
        ),
      },
    },
  );
}

export function createAssociatedLedgerAccountInstructionV5(
  programId: PublicKey,
  // staking pool
  poolId: PublicKey,
  // user
  associatedLedgerAccount: PublicKey,
  userOwner: PublicKey,
) {
  const dataLayout = struct([u8("instruction")]);

  const keys = [
    { pubkey: poolId, isSigner: false, isWritable: true },
    { pubkey: associatedLedgerAccount, isSigner: false, isWritable: true },
    { pubkey: userOwner, isSigner: true, isWritable: false },
    { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
  ];

  const data = Buffer.alloc(dataLayout.span);
  dataLayout.encode(
    {
      instruction: 10,
    },
    data,
  );

  return new TransactionInstruction({
    keys,
    programId,
    data,
  });
}
